import {
  AdAnalyticsFilter,
  AdAnalyticsVideo,
  AdAnalyticsVideoFilter,
  AdAnalyticsVideoSort,
  createSomeAdAnalyticsVideo,
} from '@kaizenplatform/hugot-entity';
import gql from 'graphql-tag';
import { List } from 'immutable';

import GraphQLClient from '../../adapter/GraphQLClient';
import WithTotalCountArrayPageInfo from '../../entity/WithTotalCountArrayPageInfo';

class AdAnalyticsVideoWebApi {
  private readonly graphqlClient: GraphQLClient;

  constructor({ graphqlClient }: { graphqlClient: GraphQLClient }) {
    this.graphqlClient = graphqlClient;
  }

  // @TODO platformt, media, placementをarrayにする
  // @TODO analyzeStateとtitleを追加

  // with adAnalyticsVideoIndex for detail page
  public getAllOfAdAnalyticsVideo = async ({
    platformKey,
    mediaKey,
    placementKey,
    period,
    adVideoIds,
    perPage,
    after,
    before,
    filters,
    sort,
    organizationId,
  }: AdAnalyticsFilter & {
    adVideoIds: string[] | null;
    filters: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
    organizationId?: string;
  }): Promise<{ adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo }> => {
    const result = await this.graphqlClient.query(
      gql`
        query adAnalyticsVideos(
          $adPlatformKeys: [AdPlatformKey!]!
          $adMediaKeys: [AdMediaKey!]!
          $adPlacementKeys: [AdPlacementKey!]!
          $period: PerformanceReportPeriodPattern!
          $adVideoIds: [ID!]
          $first: Int
          $after: String
          $before: String
          $filters: [AdAnalyticsVideoFilter!]!
          $sort: AdAnalyticsVideoSort
          $organizationId: ID!
        ) {
          adAnalyticsVideos(
            adPlatformKeys: $adPlatformKeys
            adMediaKeys: $adMediaKeys
            adPlacementKeys: $adPlacementKeys
            period: $period
            adVideoIds: $adVideoIds
            first: $first
            filters: $filters
            sort: $sort
            organizationId: $organizationId
            after: $after
            before: $before
          ) {
            edges {
              node {
                adVideoId
                analyzingState
                title
                project {
                  id
                  title
                }
                platforms {
                  key
                  name
                }
                medias {
                  key
                  name
                }
                placements {
                  key
                  name
                }
                spend
                mobileAppInstalls
                impressions
                clicks
                currency
                conversions
                conversionsKey
                viewThroughConversions
                cvr
                ctr
                cpc
                cpm
                cpa
                cpaWithViewThroughConversions
                duration
                videoUrl
                thumbnailImageUrl
                adAnalyticsVideoIndex {
                  keywords {
                    text
                    sections {
                      start
                      end
                    }
                  }
                  keyFrames {
                    thumbnailImageUrl
                    start
                    end
                  }
                  watchingRatings {
                    until
                    rate
                  }
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              startCursor
              totalCount
            }
          }
        }
      `,
      {
        adPlatformKeys: platformKey !== undefined ? platformKey : [],
        adMediaKeys: mediaKey !== undefined ? mediaKey : [],
        adPlacementKeys: placementKey !== undefined ? placementKey : [],
        period: period,
        adVideoIds: adVideoIds,
        first: perPage,
        after: after,
        before: before,
        filters: filters,
        sort: sort,
        organizationId: organizationId,
      },
    );
    if (!Array.isArray(result.adAnalyticsVideos.edges)) {
      throw new Error('result.adAnalyticsVideos.edges must be an array.');
    }

    return {
      adAnalyticsVideos: List(result.adAnalyticsVideos.edges.map((edge: any) => AdAnalyticsVideo.fromJSON(edge.node))),
      pageInfo: result.adAnalyticsVideos.pageInfo as WithTotalCountArrayPageInfo,
    };
  };

  // without adAnalyticsVideoIndex for list page
  public getAllOfAdAnalyticsVideos = async ({
    platformKey,
    mediaKey,
    placementKey,
    period,
    adVideoIds,
    perPage,
    after,
    before,
    filters,
    sort,
    organizationId,
  }: AdAnalyticsFilter & {
    adVideoIds: string[] | null;
    filters: AdAnalyticsVideoFilter[];
    sort?: AdAnalyticsVideoSort;
    perPage?: number;
    after?: string;
    before?: string;
    organizationId?: string;
  }): Promise<{ adAnalyticsVideos: List<AdAnalyticsVideo>; pageInfo: WithTotalCountArrayPageInfo }> => {
    const result = await this.graphqlClient.query(
      gql`
        query adAnalyticsVideos(
          $adPlatformKeys: [AdPlatformKey!]!
          $adMediaKeys: [AdMediaKey!]!
          $adPlacementKeys: [AdPlacementKey!]!
          $period: PerformanceReportPeriodPattern!
          $adVideoIds: [ID!]
          $first: Int
          $after: String
          $before: String
          $filters: [AdAnalyticsVideoFilter!]!
          $sort: AdAnalyticsVideoSort
          $organizationId: ID!
        ) {
          adAnalyticsVideos(
            adPlatformKeys: $adPlatformKeys
            adMediaKeys: $adMediaKeys
            adPlacementKeys: $adPlacementKeys
            period: $period
            adVideoIds: $adVideoIds
            first: $first
            filters: $filters
            sort: $sort
            organizationId: $organizationId
            after: $after
            before: $before
          ) {
            edges {
              node {
                adVideoId
                analyzingState
                title
                project {
                  id
                  title
                }
                platforms {
                  key
                  name
                }
                medias {
                  key
                  name
                }
                placements {
                  key
                  name
                }
                spend
                mobileAppInstalls
                impressions
                clicks
                currency
                conversions
                conversionsKey
                viewThroughConversions
                cvr
                ctr
                cpc
                cpm
                cpa
                cpaWithViewThroughConversions
                duration
                thumbnailImageUrl
              }
            }
            pageInfo {
              endCursor
              hasNextPage
              hasPreviousPage
              startCursor
              totalCount
            }
          }
        }
      `,
      {
        adPlatformKeys: platformKey !== undefined ? platformKey : [],
        adMediaKeys: mediaKey !== undefined ? mediaKey : [],
        adPlacementKeys: placementKey !== undefined ? placementKey : [],
        period: period,
        adVideoIds: adVideoIds,
        first: perPage,
        after: after,
        before: before,
        filters: filters,
        sort: sort,
        organizationId: organizationId,
      },
    );
    if (!Array.isArray(result.adAnalyticsVideos.edges)) {
      throw new Error('result.adAnalyticsVideos.edges must be an array.');
    }

    return {
      adAnalyticsVideos: List(result.adAnalyticsVideos.edges.map((edge: any) => AdAnalyticsVideo.fromJSON(edge.node))),
      pageInfo: result.adAnalyticsVideos.pageInfo as WithTotalCountArrayPageInfo,
    };
  };

  // Mock
  // ---------------------------------------------------------
  public getAllOfAdAnalyticsVideoMock = async (): Promise<{
    adAnalyticsVideos: List<AdAnalyticsVideo>;
    pageInfo: WithTotalCountArrayPageInfo;
  }> => {
    await new Promise((resolve) => setTimeout(resolve, 1000));
    return {
      adAnalyticsVideos: List([
        createSomeAdAnalyticsVideo({ adVideoId: '123' }),
        createSomeAdAnalyticsVideo({ adVideoId: '456' }),
      ]),
      pageInfo: {
        endCursor: null,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        totalCount: null,
      },
    };
  };
}

export default AdAnalyticsVideoWebApi;
